import React, { useState, useEffect } from 'react';
import Layout from '../components/dashboard/Layout';
import SellAssetModal from '../components/SellAssetModal';
import { assetsAPI, salesAPI } from '../services/api';
import { DollarSign, TrendingUp, Package, Banknote, ShoppingCart, CheckCircle } from 'lucide-react';

const SalesPage = () => {
  const [assets, setAssets] = useState([]);
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showSellModal, setShowSellModal] = useState(false);
  const [activeTab, setActiveTab] = useState('sell'); // 'sell' or 'history'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assetsRes, salesRes, statsRes] = await Promise.all([
        assetsAPI.getAll(),
        salesAPI.getAll(100),
        salesAPI.getStats()
      ]);

      setAssets(assetsRes.data);
      setSales(salesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSellClick = (asset) => {
    setSelectedAsset(asset);
    setShowSellModal(true);
  };

  const handleSellSuccess = () => {
    loadData();
  };

  // Filter assets that can be sold (ready status)
  const sellableAssets = assets.filter(a => a.status === 'ready');
  const soldAssets = assets.filter(a => a.status === 'sold');

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">💰 Jual Beli Unit</h1>
          <p className="text-muted-foreground">Kelola penjualan laptop dan asset</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="glass-card p-5 rounded-xl border border-border/40">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 rounded-lg bg-secondary/10">
                <TrendingUp className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Penjualan</p>
                <p className="text-2xl font-bold text-secondary">
                  {stats?.total_units_sold || 0} Unit
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Bulan ini: {stats?.monthly_sales || 0} unit
            </p>
          </div>

          <div className="glass-card p-5 rounded-xl border border-border/40">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Banknote className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-primary">
                  Rp {((stats?.total_revenue || 0) / 1000000).toFixed(1)}M
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Bulan ini: Rp {((stats?.monthly_revenue || 0) / 1000).toLocaleString('id-ID')}
            </p>
          </div>

          <div className="glass-card p-5 rounded-xl border border-border/40">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Profit</p>
                <p className="text-2xl font-bold text-green-500">
                  Rp {((stats?.total_profit || 0) / 1000000).toFixed(1)}M
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Margin: {stats?.average_profit_margin?.toFixed(1) || 0}%
            </p>
          </div>

          <div className="glass-card p-5 rounded-xl border border-border/40">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Package className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ready to Sell</p>
                <p className="text-2xl font-bold text-blue-500">
                  {sellableAssets.length} Unit
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Terjual: {soldAssets.length} unit
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 border-b border-border">
          <button
            onClick={() => setActiveTab('sell')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'sell'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ShoppingCart className="w-4 h-4 inline mr-2" />
            Jual Unit ({sellableAssets.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'history'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Riwayat Penjualan ({sales.length})
          </button>
        </div>

        {/* Sell Tab */}
        {activeTab === 'sell' && (
          <div className="glass-card p-6 rounded-xl border border-border/40">
            <h2 className="text-xl font-semibold text-foreground mb-4">Unit Siap Dijual</h2>
            
            {sellableAssets.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground font-medium">Tidak ada unit yang ready untuk dijual</p>
                <p className="text-sm text-muted-foreground mt-1">Unit dengan status &quot;Ready&quot; akan muncul di sini</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sellableAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="p-4 rounded-lg border-2 border-border bg-background/50 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-foreground text-lg">
                          {asset.brand} {asset.model}
                        </p>
                        <p className="text-sm text-muted-foreground">{asset.serial_number}</p>
                      </div>
                      <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded">
                        {asset.category}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Harga Beli:</span>
                        <span className="font-medium text-foreground">
                          Rp {(asset.purchase_price / 1000).toFixed(0)}K
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Nilai Sekarang:</span>
                        <span className="font-semibold text-primary">
                          Rp {(asset.current_value / 1000).toFixed(0)}K
                        </span>
                      </div>
                      {asset.inventory_number && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Inventory:</span>
                          <span className="text-foreground">{asset.inventory_number}</span>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => handleSellClick(asset)}
                      className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2 font-medium"
                    >
                      <DollarSign className="w-4 h-4" />
                      <span>Jual Unit Ini</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="glass-card p-6 rounded-xl border border-border/40">
            <h2 className="text-xl font-semibold text-foreground mb-4">Riwayat Penjualan</h2>
            
            {sales.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground font-medium">Belum ada riwayat penjualan</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sales.map((sale) => (
                  <div
                    key={sale.id}
                    className="p-4 rounded-lg border border-border bg-background/50 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      {/* Left Side */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-foreground">
                              {sale.asset_details?.brand} {sale.asset_details?.model}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              SN: {sale.asset_details?.serial_number}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            sale.payment_status === 'paid' 
                              ? 'bg-secondary/10 text-secondary'
                              : 'bg-yellow-500/10 text-yellow-600'
                          }`}>
                            {sale.payment_status === 'paid' ? 'Lunas' : sale.payment_status}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-muted-foreground">Pembeli</p>
                            <p className="font-medium text-foreground">{sale.buyer_name}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Harga Jual</p>
                            <p className="font-bold text-primary">
                              Rp {(sale.sale_price / 1000).toFixed(0)}K
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Profit</p>
                            <p className="font-bold text-secondary">
                              Rp {((sale.sale_price - (sale.purchase_price || 0)) / 1000).toFixed(0)}K
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Tanggal</p>
                            <p className="text-foreground">
                              {new Date(sale.sale_date).toLocaleDateString('id-ID')}
                            </p>
                          </div>
                        </div>

                        <div className="mt-2 text-xs text-muted-foreground">
                          <span>📞 {sale.buyer_phone}</span>
                          {sale.buyer_email && <span className="ml-3">📧 {sale.buyer_email}</span>}
                        </div>
                      </div>

                      {/* Right Side - Profit Margin */}
                      <div className="text-center md:text-right">
                        <p className="text-xs text-muted-foreground">Margin</p>
                        <p className="text-2xl font-bold text-green-500">
                          {sale.profit_margin?.toFixed(1) || 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sell Asset Modal */}
        {showSellModal && selectedAsset && (
          <SellAssetModal
            asset={selectedAsset}
            onClose={() => {
              setShowSellModal(false);
              setSelectedAsset(null);
            }}
            onSuccess={handleSellSuccess}
          />
        )}
      </div>
    </Layout>
  );
};

export default SalesPage;
